import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Users, Bell, GraduationCap, Headphones, Layers, Sparkles, Send, Loader2, CheckCircle2, Star, Shield, Globe2 } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";

/**
 * Book My Tutor App promotion — premium clean SaaS-style layout, no device mockups.
 * All copy + store URLs come from Admin → Site Settings → App Management.
 */

const FEATURES = [
    { icon: Users, title: "Parent & Tutor Management", desc: "One integrated dashboard for both sides — no WhatsApp chaos." },
    { icon: MessageSquare, title: "Easy Communication", desc: "In-app messaging keeps every class conversation organised." },
    { icon: Bell, title: "Class Updates & Scheduling", desc: "Automatic reminders, reschedule requests, live calendar sync." },
    { icon: GraduationCap, title: "Student Tracking", desc: "Attendance, progress notes, test scores — all in one place." },
    { icon: Headphones, title: "Fast Support System", desc: "Raise a ticket inside the app, resolved by the Anand team." },
    { icon: Layers, title: "Centralised Learning", desc: "Study plans, homework, shared resources — never lost again." },
];

const TRUST_POINTS = [
    { icon: Users,  label: "50,000+ families" },
    { icon: Star,   label: "4.8 ★ parent rating" },
    { icon: Shield, label: "Verified tutors only" },
    { icon: Globe2, label: "Used across 27 countries" },
];

export default function BookMyTutorPromo({ variant = "full" }) {
    const { settings } = useSettings();
    if (settings.app_promo_enabled === false) return null;

    const heading = settings.app_promo_heading || "Manage Classes Seamlessly with Book My Tutor App";
    const subheading = settings.app_promo_subheading || "Parents and tutors can easily manage classes, communication, schedules, updates and support through our official Book My Tutor platform.";
    const playUrl = settings.app_promo_playstore_url || "https://www.bookmytutor.com/download";
    const appleUrl = settings.app_promo_appstore_url || "https://www.bookmytutor.com/download";

    // Compact variant used in footer etc
    if (variant === "compact") {
        return (
            <div data-testid="bmt-app-promo-compact" className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400">Our App</div>
                </div>
                <div className="text-white font-heading font-semibold text-sm mb-2">Book My Tutor</div>
                <p className="text-xs text-slate-300 leading-relaxed mb-3">Class, payment & progress management for parents and tutors.</p>
                <div className="flex gap-2">
                    <a data-testid="bmt-footer-playstore" href={playUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-black text-white text-[10px] font-semibold hover:bg-slate-800 transition-colors">
                        <PlayStoreIcon className="w-3.5 h-3.5" /> Play Store
                    </a>
                    <a data-testid="bmt-footer-appstore" href={appleUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-black text-white text-[10px] font-semibold hover:bg-slate-800 transition-colors">
                        <AppleIcon className="w-3.5 h-3.5" /> App Store
                    </a>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <meta name="keywords" content="Tutor Management App, Parent Tutor Communication App, Online Tuition Management Platform, Book My Tutor App, Home Tuition Management System" />
            </Helmet>
            <section
                data-testid="bmt-app-promo"
                aria-labelledby="bmt-heading"
                className="relative py-20 sm:py-28 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white"
            >
                {/* Decorative glow — lightweight, no images */}
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #fbbf24 0%, transparent 45%), radial-gradient(circle at 85% 90%, #3B82F6 0%, transparent 40%)" }} />
                <div className="absolute inset-0 opacity-[0.04] bg-grid pointer-events-none" />

                <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
                    {/* Eyebrow */}
                    <div className="text-center mb-5">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/25 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300">
                            <Sparkles className="w-3 h-3" /> Official Platform · Book My Tutor
                        </div>
                    </div>

                    {/* Heading + subheading centered */}
                    <div className="text-center mb-10">
                        <h2 id="bmt-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight max-w-3xl mx-auto">
                            {heading}
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-blue-100/85 leading-relaxed max-w-2xl mx-auto">
                            {subheading}
                        </p>
                    </div>

                    {/* Trust strip */}
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-12 pb-10 border-b border-white/10">
                        {TRUST_POINTS.map((t) => (
                            <div key={t.label} className="inline-flex items-center gap-2 text-xs sm:text-sm text-blue-100/80">
                                <t.icon className="w-4 h-4 text-amber-300" />
                                <span className="font-semibold">{t.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Feature grid — 3 cols desktop, 2 tablet, 1 mobile */}
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-12">
                        {FEATURES.map((f) => (
                            <li
                                key={f.title}
                                className="group flex items-start gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-amber-400/25 hover:-translate-y-0.5 transition-all backdrop-blur-sm"
                            >
                                <div className="w-10 h-10 rounded-xl bg-amber-400/15 text-amber-300 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:bg-amber-400/25 transition-all">
                                    <f.icon className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-heading font-semibold text-[15px] mb-1">{f.title}</div>
                                    <div className="text-xs text-blue-200/80 leading-relaxed">{f.desc}</div>
                                </div>
                            </li>
                        ))}
                    </ul>

                    {/* Store buttons — centered */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
                        <a
                            data-testid="bmt-playstore"
                            href={playUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Book My Tutor on Google Play"
                            className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto px-6 py-3.5 rounded-xl bg-black hover:bg-slate-900 text-white border border-white/10 hover:border-amber-400/40 hover:-translate-y-0.5 transition-all shadow-xl"
                        >
                            <PlayStoreIcon className="w-7 h-7 group-hover:scale-110 transition-transform" />
                            <div className="text-left leading-tight">
                                <div className="text-[10px] uppercase tracking-wider text-white/70">Get it on</div>
                                <div className="font-heading font-semibold text-base">Google Play</div>
                            </div>
                        </a>
                        <a
                            data-testid="bmt-appstore"
                            href={appleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Book My Tutor on the App Store"
                            className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto px-6 py-3.5 rounded-xl bg-black hover:bg-slate-900 text-white border border-white/10 hover:border-amber-400/40 hover:-translate-y-0.5 transition-all shadow-xl"
                        >
                            <AppleIcon className="w-7 h-7 group-hover:scale-110 transition-transform" />
                            <div className="text-left leading-tight">
                                <div className="text-[10px] uppercase tracking-wider text-white/70">Download on the</div>
                                <div className="font-heading font-semibold text-base">App Store</div>
                            </div>
                        </a>
                    </div>

                    {/* Lead-capture mini-form — admin-toggleable */}
                    {settings.app_promo_lead_capture_enabled !== false && (
                        <div className="max-w-2xl mx-auto">
                            <AppLeadForm playUrl={playUrl} appleUrl={appleUrl} />
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}

/* ---------- Store glyphs ---------- */
const PlayStoreIcon = ({ className = "" }) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden>
        <path d="M3.6 2.3c-.4.3-.6.8-.6 1.5v16.4c0 .7.2 1.2.6 1.5l9.7-9.7L3.6 2.3z" fill="#00C3FF" />
        <path d="M16 11.2 13.3 12l-2.6-2.6L16 11.2z" fill="#FFBC00" />
        <path d="M3.6 2.3 13.3 12 16 11.2l5-2.9c.7-.4 1-1.1.3-1.8L4.2 2.2c-.2-.1-.4 0-.6.1z" fill="#00D971" />
        <path d="M3.6 21.7c.2.1.4.2.6.1l16.1-4.3c.7-.7.4-1.4-.3-1.8l-5-2.9L13.3 12l-9.7 9.7z" fill="#FB3C4F" />
    </svg>
);

const AppleIcon = ({ className = "" }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        <path d="M17.05 20.28c-.98.95-2.05.86-3.08.38-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.38C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);

/* ---------- Lead-capture mini-form ---------- */
function AppLeadForm({ playUrl, appleUrl }) {
    const location = useLocation();
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        const cleaned = phone.replace(/\D/g, "");
        if (cleaned.length < 10) {
            toast.error("Please enter a valid 10-digit mobile number.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/app-leads", {
                phone: cleaned,
                email: email.trim() || undefined,
                name: name.trim() || undefined,
                source_page: location.pathname,
            });
            setDone(true);
            toast.success("Got it — download links coming your way!");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div data-testid="bmt-lead-success" className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5 backdrop-blur-sm">
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-heading font-semibold text-emerald-200 mb-1">You're on the list!</div>
                        <p className="text-xs text-emerald-100/90 leading-relaxed mb-3">We've saved your details and emailed the install links. You can also tap below right now:</p>
                        <div className="flex flex-wrap gap-2">
                            <a href={playUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black text-white text-xs font-semibold hover:bg-slate-800 transition-colors">
                                <PlayStoreIcon className="w-3.5 h-3.5" /> Open Google Play
                            </a>
                            <a href={appleUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black text-white text-xs font-semibold hover:bg-slate-800 transition-colors">
                                <AppleIcon className="w-3.5 h-3.5" /> Open App Store
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="bmt-lead-form"
            className="mt-6 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-5"
        >
            <div className="flex items-center gap-2 mb-3">
                <Send className="w-4 h-4 text-amber-300" />
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-300">Send me the download link</div>
            </div>
            <p className="text-xs text-blue-100/80 mb-4 leading-relaxed">Drop your number and we'll send you the install link instantly. Optional email gets you the full setup guide.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    data-testid="bmt-lead-name"
                    className="px-3 py-2.5 text-sm rounded-lg bg-white/95 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Mobile number *"
                    required
                    data-testid="bmt-lead-phone"
                    className="px-3 py-2.5 text-sm rounded-lg bg-white/95 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email (optional, for setup guide)"
                    data-testid="bmt-lead-email"
                    className="flex-1 px-3 py-2.5 text-sm rounded-lg bg-white/95 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="bmt-lead-submit"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold hover:bg-amber-300 disabled:opacity-60 transition-colors shadow-lg"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? "Sending…" : "Send me the link"}
                </button>
            </div>
            <p className="text-[10px] text-blue-200/60 mt-3">By submitting, you agree to receive Anand Tutorials communications. We never spam — unsubscribe anytime.</p>
        </form>
    );
}
